import { ENTER_KEY, SPACE_KEY } from 'constants/keyCodes';
import { KeyboardEvent } from 'react';
import { BasicOption } from './BasicOption';
import { DEFAULT_CATEGORY_ID } from './constants';
import { LoadingOption } from './LoadingOption';
import {
  CategoryHeader,
  CategorySeparator,
  DoubleLine,
  OptionLabel,
  OptionsListCategoryLabel,
  SelectDropdownInfoText,
} from './Selects.styles';

import { InfiniteScrolling } from './InfiniteScrolling';
import { OptionsListProps, SelectValueType } from './types';

export function OptionsList<TValue extends SelectValueType = string>({
  Option = BasicOption,
  canFetch = false,
  categoryDetail,
  categoryLabels = {},
  containerRef,
  emptyDropdownDataTestId,
  emptyStateText,
  filterText,
  getOptions,
  id,
  isOpen,
  onFetch = () => undefined,
  singleSelect,
  toggleDropdown,
  isLoading = false,
  showCategoryDivider = false,
  EmptyState,
}: OptionsListProps<TValue>) {
  const categoryDetailKeys = Object.keys(categoryDetail);
  const hasCategories =
    categoryDetailKeys.length > 1 ||
    (categoryDetailKeys.length === 1 && categoryDetailKeys[0] !== DEFAULT_CATEGORY_ID);

  const showEmptyState = !getOptions(undefined, filterText).length && !canFetch && !isLoading;
  const shouldDisplayCategoryHeader = hasCategories && !showEmptyState;
  const categoryDetailValues = Object.values(categoryDetail);

  return (
    <>
      {showEmptyState &&
        (EmptyState && !filterText ? (
          <EmptyState />
        ) : (
          <SelectDropdownInfoText data-test={emptyDropdownDataTestId}>
            {emptyStateText}
          </SelectDropdownInfoText>
        ))}
      {isLoading ? (
        <LoadingOption />
      ) : (
        categoryDetailValues.map(({ categoryId }, index) => {
          const supportCategorySeparator =
            categoryDetailValues.length > 1 && index < categoryDetailValues.length - 1;
          const shouldShowCategorySeparator =
            supportCategorySeparator && shouldDisplayCategoryHeader && showCategoryDivider;

          return (
            <div key={`${categoryId}-category-header`} data-test="options-list-container">
              {shouldDisplayCategoryHeader && (
                <CategoryHeader $showCategoryDivider={showCategoryDivider}>
                  <OptionsListCategoryLabel>
                    {categoryLabels[categoryId] || categoryId || ''}
                  </OptionsListCategoryLabel>
                </CategoryHeader>
              )}
              {getOptions(categoryId, filterText).map((option) => {
                const selectOption = () => {
                  singleSelect(categoryId, option.value);
                  toggleDropdown(false);
                };

                return (
                  <Option
                    {...option}
                    aria-selected={option.isSelected}
                    aria-disabled={option.isDisabled}
                    data-test={`${id}-option`}
                    key={option.uniqueId ?? option.id}
                    onKeyDown={({ keyCode }: KeyboardEvent) => {
                      if ([ENTER_KEY, SPACE_KEY].includes(keyCode)) {
                        selectOption();
                      }
                    }}
                    id={option.uniqueId ?? option.id}
                    onClick={selectOption}
                    role="option"
                    tabIndex={isOpen ? 0 : undefined}
                  >
                    <OptionLabel>{option.label}</OptionLabel>
                    {option.doubleLine && (
                      <DoubleLine data-test="select-double-line" isSelected={option.isSelected}>
                        {option.doubleLine}
                      </DoubleLine>
                    )}
                  </Option>
                );
              })}
              {shouldShowCategorySeparator && <CategorySeparator />}
            </div>
          );
        })
      )}
      <InfiniteScrolling
        isOpen={isOpen}
        canFetch={canFetch}
        onFetch={onFetch}
        containerRef={containerRef}
      />
    </>
  );
}
